/**
 * useConciergeChat — Custom SSE hook replacing useChat from @ai-sdk/react.
 *
 * Connects to the backend `/concierge/agent/stream` SSE endpoint,
 * parses Mastra-format SSE events, and builds UIMessage objects.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getHeadersWithUniqueVisitorId } from '../../Auth';
import type { UIMessage, UIMessagePart } from '../types/mastraTypes';
import type { ChatStatus } from '../types/mastraTypes';
import { fetchFollowUpSuggestions, retryConversation, stopConversation } from '../api';
import type { ConciergeDevOptions } from '../types/devOptions';
import { createInFlightSendLock } from './inFlightSendLock';
import { bindReservedTouchToConversation, reserveConciergeTouchId } from './conciergeTouchToken';

const LOG_PREFIX = '[useConciergeChat]';

function previewPrompt(text: string | undefined): string {
  if (!text) return '';
  const trimmed = text.replace(/\s+/g, ' ').trim();
  return trimmed.length <= 80 ? trimmed : `${trimmed.slice(0, 80)}…`;
}

function extractFollowUpsFromMessage(msg: UIMessage | undefined): string[] {
  if (!msg || msg.role !== 'assistant') return [];
  const parts = msg.parts as any[];
  if (Array.isArray(parts)) {
    for (const p of parts) {
      if (p?.type === 'tool-result' && p?.toolName === 'suggest_follow_ups') {
        const accepted = p.result?.accepted;
        if (Array.isArray(accepted)) {
          return accepted.filter((s: unknown): s is string => typeof s === 'string').slice(0, 3);
        }
      }
    }
    const followUpParts = parts.filter((p: any) => p?.type === 'data-follow-ups');
    if (followUpParts.length > 0) {
      const last = followUpParts[followUpParts.length - 1];
      const raw = last?.data?.questions ?? last?.questions;
      if (Array.isArray(raw)) {
        return raw.filter((s: unknown): s is string => typeof s === 'string').slice(0, 3);
      }
    }
  }

  const topLevel = (msg as any).followUpSuggestions;
  if (Array.isArray(topLevel)) {
    return topLevel.filter((s: unknown): s is string => typeof s === 'string').slice(0, 3);
  }

  const meta = msg.metadata;
  if (meta && typeof meta === 'object' && Array.isArray(meta.followUpSuggestions)) {
    return meta.followUpSuggestions.filter((s: unknown): s is string => typeof s === 'string').slice(0, 3);
  }
  return [];
}

function generateMessageId(): string {
  return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export interface UseConciergeOptions {
  apiBaseUrl: string;
  conversationId?: string | null;
  sharedConversationId?: string | null;
  brandId?: string | null;
  appId?: string | null;
  devOptions?: ConciergeDevOptions;
  pdpStacklineSku?: string | null;
  isOnChatWidget?: boolean;
  temporaryChat?: boolean;
  onError?: (error: Error) => void;
  onTripwire?: (message: string) => void;
  onConversationIdGenerated?: (id: string) => void;
}

export function useConciergeChat(options: UseConciergeOptions) {
  const {
    apiBaseUrl,
    conversationId,
    sharedConversationId,
    appId,
    devOptions,
    pdpStacklineSku,
    isOnChatWidget,
    temporaryChat,
    onError,
    onTripwire,
    onConversationIdGenerated,
  } = options;

  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const sharedConversationIdRef = useRef(sharedConversationId);
  sharedConversationIdRef.current = sharedConversationId;

  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [status, setStatus] = useState<ChatStatus>('ready');
  const [transientStatus, setTransientStatus] = useState<string | null>(null);
  const [tripwireMessage, setTripwireMessage] = useState<string | null>(null);
  const [latestFollowUps, setLatestFollowUps] = useState<string[]>([]);
  const [isLoadingFollowUps, setIsLoadingFollowUps] = useState(false);
  const lastHydratedFollowUpsMessageIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sendLockRef = useRef(createInFlightSendLock());

  useEffect(() => {
    if (status !== 'streaming') setTransientStatus(null);
  }, [status]);

  useEffect(() => {
    setLatestFollowUps([]);
    setIsLoadingFollowUps(false);
    lastHydratedFollowUpsMessageIdRef.current = null;
  }, [conversationId]);

  // Hydrate chips from history messages when present; skip empty so a history
  // reload (new message ids) doesn't wipe post-stream fetch results.
  useEffect(() => {
    if (status !== 'ready') return;
    const lastAssistantMsg = [...messages].reverse().find((m) => m.role === 'assistant');
    if (!lastAssistantMsg?.id) return;
    if (lastAssistantMsg.id === lastHydratedFollowUpsMessageIdRef.current) return;

    const fromMessage = extractFollowUpsFromMessage(lastAssistantMsg);
    if (fromMessage.length === 0) return;

    lastHydratedFollowUpsMessageIdRef.current = lastAssistantMsg.id;
    setLatestFollowUps(fromMessage);
    setIsLoadingFollowUps(false);
  }, [status, messages]);

  const fetchFollowUpsForConversation = useCallback(
    async (convId: string) => {
      setIsLoadingFollowUps(true);
      setLatestFollowUps([]);
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const questions = await fetchFollowUpSuggestions(convId, {
          headersToInject: headers,
          apiBaseUrl,
          devOptions: { promptLabel: (devOptions as { promptLabel?: string } | undefined)?.promptLabel },
        });
        if (conversationIdRef.current !== convId) return;
        setLatestFollowUps(questions);
      } catch {
        if (conversationIdRef.current === convId) setLatestFollowUps([]);
      } finally {
        if (conversationIdRef.current === convId) setIsLoadingFollowUps(false);
      }
    },
    [apiBaseUrl, devOptions],
  );

  const stop = useCallback(() => {
    sendLockRef.current.cancel();
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsLoadingFollowUps(false);
    setStatus('ready');
  }, []);

  const processSSEStream = useCallback(
    async (response: Response) => {
      const reader = response.body?.getReader();
      if (!reader) return;

      const decoder = new TextDecoder();
      let buffer = '';
      const assistantId = generateMessageId();
      const parts: UIMessagePart[] = [];
      const currentTextParts: Map<string, string> = new Map();

      const updateAssistantMessage = () => {
        const collectedParts: UIMessagePart[] = [...parts];
        currentTextParts.forEach((text) => {
          if (text) collectedParts.push({ type: 'text', text });
        });
        setMessages((prev) => {
          const existing = prev.findIndex((m) => m.id === assistantId);
          const msg: UIMessage = { id: assistantId, role: 'assistant', parts: collectedParts };
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = msg;
            return updated;
          }
          return [...prev, msg];
        });
      };

      const handleSSEEvent = (event: string, data: any) => {
        switch (event) {
          case 'text-start':
            currentTextParts.set(data.id, '');
            break;

          case 'text-delta':
            currentTextParts.set(data.id, (currentTextParts.get(data.id) || '') + (data.delta || ''));
            updateAssistantMessage();
            break;

          case 'text-end': {
            const text = currentTextParts.get(data.id) || '';
            currentTextParts.delete(data.id);
            if (text) parts.push({ type: 'text', text });
            updateAssistantMessage();
            break;
          }

          case 'tool-call':
            parts.push({
              type: 'tool-call',
              toolCallId: data.toolCallId,
              toolName: data.toolName,
              args: data.args,
            });
            updateAssistantMessage();
            break;

          case 'tool-result':
            parts.push({
              type: 'tool-result',
              toolCallId: data.toolCallId,
              toolName: data.toolName,
              result: data.result,
            });
            updateAssistantMessage();
            break;

          case 'tripwire': {
            const reason = data.reason || data.message;
            if (reason) {
              setTripwireMessage(reason);
              onTripwire?.(reason);
            }
            break;
          }

          case 'finish':
            break;

          default:
            if (event.startsWith('data-')) {
              if (event === 'data-conversation-id') {
                const serverConversationId = data?.conversationId;
                if (serverConversationId && serverConversationId !== conversationIdRef.current) {
                  conversationIdRef.current = serverConversationId;
                  // This turn already sent a touch the agent is stamping on its
                  // product events. Bind it to the id the server just named, so the
                  // qualifying event reuses it instead of minting a rival token.
                  bindReservedTouchToConversation(appId ?? undefined, serverConversationId);
                  onConversationIdGenerated?.(serverConversationId);
                }
              } else if (event === 'data-status') {
                setTransientStatus(data?.status ?? null);
              } else if (event === 'data-tripwire') {
                const message = data?.message;
                if (message) {
                  setTripwireMessage(message);
                  onTripwire?.(message);
                }
              } else {
                parts.push({ type: event, data } as any);
                updateAssistantMessage();
              }
            }
            break;
        }
      };

      try {
        let streamDone = false;
        let currentEvent = '';
        let currentData = '';
        let chunkIndex = 0;
        let eventChunk = 0;
        while (!streamDone) {
          const { done, value } = await reader.read();
          if (done) {
            streamDone = true;
            break;
          }
          chunkIndex++;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
              eventChunk = chunkIndex;
            } else if (line.startsWith('data: ')) {
              currentData = line.slice(6);
            } else if (line === '' && currentEvent && currentData) {
              if (chunkIndex !== eventChunk) {
                console.warn('[SSE] event split across chunks:', currentEvent, `(event in chunk ${eventChunk}, data in chunk ${chunkIndex})`);
              }
              try {
                const parsed = JSON.parse(currentData);
                handleSSEEvent(currentEvent, parsed);
              } catch {
                // skip malformed
              }
              currentEvent = '';
              currentData = '';
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    },
    [onConversationIdGenerated, onTripwire],
  );

  const sendMessage = useCallback(
    async (prompt: string | { text: string; files?: Array<{ type: 'file'; mediaType: string; url: string }> }) => {
      const text = typeof prompt === 'string' ? prompt : prompt.text;
      const files = typeof prompt === 'string' ? undefined : prompt.files;

      // An attachment is a message on its own — the composer enables Send as
      // soon as an image is attached, and bailing out here on empty text is
      // what made "send a screenshot with no caption" do nothing at all.
      if ((!text || text.trim().length === 0) && !files?.length) return;

      const sendToken = sendLockRef.current.tryBegin();
      if (sendToken === null) {
        console.log(`${LOG_PREFIX} Dropped duplicate send while a turn is in flight`, {
          conversationId: conversationIdRef.current,
          promptPreview: previewPrompt(text),
        });
        return;
      }

      const promptPreview = previewPrompt(text);
      console.log(`${LOG_PREFIX} Send started`, {
        conversationId: conversationIdRef.current,
        sendToken,
        promptPreview,
      });

      setTripwireMessage(null);
      setLatestFollowUps([]);
      setIsLoadingFollowUps(false);
      lastHydratedFollowUpsMessageIdRef.current = null;

      // No empty text part on an image-only turn — it would render as a blank
      // bubble above the picture.
      const userParts: UIMessagePart[] = text ? [{ type: 'text', text }] : [];
      if (files?.length) {
        for (const f of files) {
          userParts.push({ type: 'file', mediaType: f.mediaType, url: f.url } as any);
        }
      }
      const userMessage: UIMessage = { id: generateMessageId(), role: 'user', parts: userParts };
      setMessages((prev) => [...prev, userMessage]);
      setStatus('submitted');

      const controller = new AbortController();
      abortControllerRef.current = controller;

      let sendOutcome: 'ok' | 'error' | 'abort' = 'ok';
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const conciergeTouchId = reserveConciergeTouchId(appId ?? undefined, conversationIdRef.current ?? null);
        const res = await fetch(`${apiBaseUrl}/api-rewards/concierge/agent/stream`, {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
          },
          body: JSON.stringify({
            prompt: text,
            conversationId: conversationIdRef.current,
            sharedConversationId: sharedConversationIdRef.current,
            appId,
            devOptions,
            ...(pdpStacklineSku ? { pdpStacklineSku } : {}),
            ...(isOnChatWidget ? { isOnChatWidget: true } : {}),
            ...(temporaryChat ? { temporaryChat: true } : {}),
            ...(files?.length ? { fileParts: files } : {}),
            // The agent stamps this onto the `product_suggested` events it emits
            // server side, which is the only way a product path that renders no
            // client card can still be attributed. Reserved, not minted: it must
            // not become the active touch until the conversation qualifies.
            ...(conciergeTouchId ? { conciergeTouchId } : {}),
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(`Stream request failed: ${res.status} ${res.statusText}`);
        }

        setStatus('streaming');
        await processSSEStream(res);

        const convId = conversationIdRef.current;
        if (convId && !controller.signal.aborted) {
          void fetchFollowUpsForConversation(convId);
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          sendOutcome = 'abort';
          return;
        }
        sendOutcome = 'error';
        console.error(`${LOG_PREFIX} Stream error`, {
          conversationId: conversationIdRef.current,
          sendToken,
          promptPreview,
          error: err,
        });
        setIsLoadingFollowUps(false);
        onError?.(err instanceof Error ? err : new Error(String(err)));
        setStatus('error');
        return;
      } finally {
        sendLockRef.current.end(sendToken);
        abortControllerRef.current = null;
        setStatus((prev) => (prev === 'error' ? prev : 'ready'));
        console.log(`${LOG_PREFIX} Send finished`, {
          conversationId: conversationIdRef.current,
          sendToken,
          promptPreview,
          outcome: sendOutcome,
        });
      }
    },
    [
      apiBaseUrl,
      appId,
      devOptions,
      pdpStacklineSku,
      isOnChatWidget,
      temporaryChat,
      onError,
      processSSEStream,
      fetchFollowUpsForConversation,
    ],
  );

  const trimLocalMessagesFromLastUser = useCallback(() => {
    setMessages((prev) => {
      const lastUserIdx = prev.map((m) => m.role).lastIndexOf('user');
      if (lastUserIdx === -1) return prev;
      return prev.slice(0, lastUserIdx);
    });
  }, []);

  const getLastUserPromptFromMessages = useCallback((): string => {
    const lastUserMessage = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUserMessage) return '';
    return (lastUserMessage.parts || [])
      .filter((p: any) => p.type === 'text' && typeof p.text === 'string')
      .map((p: any) => p.text)
      .join('')
      .trim();
  }, [messages]);

  const retryLastMessage = useCallback(async () => {
    const convId = conversationIdRef.current;
    if (!convId) return;
    try {
      const headers = await getHeadersWithUniqueVisitorId();
      const { prompt, fileParts } = await retryConversation({
        conversationId: convId,
        headersToInject: headers,
        apiBaseUrl,
      });
      setMessages((prev) => {
        const lastAssistantIdx = prev.map((m) => m.role).lastIndexOf('assistant');
        if (lastAssistantIdx === -1) return prev;
        const lastUserIdx = prev.slice(0, lastAssistantIdx).map((m) => m.role).lastIndexOf('user');
        const cutFrom = lastUserIdx !== -1 ? lastUserIdx : lastAssistantIdx;
        return prev.slice(0, cutFrom);
      });
      setTripwireMessage(null);
      // Replay the attachments, not just the text. "Try Again" used to requeue
      // the prompt alone, so a shopper retrying a failed send got their message
      // through with the screenshots quietly missing (PetArmor QA row 52).
      await sendMessage(
        fileParts?.length
          ? {
              text: prompt,
              files: fileParts.map((f) => ({ type: 'file' as const, mediaType: f.mediaType, url: f.url })),
            }
          : prompt,
      );
    } catch (error) {
      console.error('[useConciergeChat] retryLastMessage failed:', error);
      onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }, [apiBaseUrl, onError, sendMessage]);

  const cancelLatestTurn = useCallback(async (): Promise<string | null> => {
    stop();
    const fallbackPrompt = getLastUserPromptFromMessages();
    const convId = conversationIdRef.current;
    if (!convId) {
      trimLocalMessagesFromLastUser();
      setTripwireMessage(null);
      return fallbackPrompt || null;
    }
    try {
      const headers = await getHeadersWithUniqueVisitorId();
      const { prompt } = await stopConversation({
        conversationId: convId,
        headersToInject: headers,
        apiBaseUrl,
      });
      trimLocalMessagesFromLastUser();
      setTripwireMessage(null);
      return (prompt || fallbackPrompt || '').trim() || null;
    } catch (error) {
      console.error('[useConciergeChat] cancelLatestTurn failed:', error);
      onError?.(error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }, [apiBaseUrl, getLastUserPromptFromMessages, onError, stop, trimLocalMessagesFromLastUser]);

  const isStreaming = status === 'streaming';
  const isLoading = status === 'submitted';

  return {
    messages,
    setMessages,
    status,
    stop,
    sendMessage,
    retryLastMessage,
    cancelLatestTurn,
    isStreaming,
    isLoading,
    isWaiting: isLoading || isStreaming,
    transientStatus,
    tripwireMessage,
    latestFollowUps,
    isLoadingFollowUps,
  };
}

export type ConciergeChat = ReturnType<typeof useConciergeChat>;
